import { z } from 'zod';
import { AssignmentsSchema } from './assignments.js';
import { DateOnlySchema, IdSchema, ListQuerySchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const TaskStatusSchema = z.enum([
    'todo',
    'in_progress',
    'blocked',
    'completed',
    'cancelled',
]);
export const TaskPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
/** Assignable work with one accountable owner and collaborators (specification 8.10, 6.3). */
export const TaskSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    projectId: IdSchema.nullable(),
    parentTaskId: IdSchema.nullable(),
    jobFileId: IdSchema.nullable(),
    milestoneId: IdSchema.nullable(),
    companyId: IdSchema.nullable(),
    contactId: IdSchema.nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: TaskStatusSchema,
    priority: TaskPrioritySchema,
    dueDate: DateOnlySchema.nullable(),
    assignments: AssignmentsSchema,
    completedAt: TimestampSchema.nullable(),
    completedByMembershipId: IdSchema.nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateTaskRequestSchema = z.strictObject({
    projectId: IdSchema.nullable().default(null),
    parentTaskId: IdSchema.nullable().default(null),
    jobFileId: IdSchema.nullable().default(null),
    milestoneId: IdSchema.nullable().default(null),
    companyId: IdSchema.nullable().default(null),
    contactId: IdSchema.nullable().default(null),
    title: z.string().trim().min(1).max(300),
    description: z.string().trim().max(10_000).nullable().default(null),
    status: z.enum(['todo', 'in_progress', 'blocked']).default('todo'),
    priority: TaskPrioritySchema.default('normal'),
    dueDate: DateOnlySchema.nullable().default(null),
    owner: IdSchema.nullable().default(null),
    collaborators: z.array(IdSchema).max(20).default([]),
});
/** Completion and cancellation are commands; status edits stay within the open states. */
export const UpdateTaskRequestSchema = CreateTaskRequestSchema.omit({
    owner: true,
    collaborators: true,
})
    .partial()
    .extend({ version: VersionSchema });
export const TaskListQuerySchema = ListQuerySchema.extend({
    status: TaskStatusSchema.optional(),
    projectId: IdSchema.optional(),
    jobFileId: IdSchema.optional(),
    milestoneId: IdSchema.optional(),
    assignedMembershipId: IdSchema.optional(),
    dueFrom: DateOnlySchema.optional(),
    dueTo: DateOnlySchema.optional(),
    includeClosed: z.stringbool().default(false),
});
export const TaskListSchema = z.strictObject({ items: z.array(TaskSchema), page: PageInfoSchema });
export const TaskVersionCommandSchema = z.strictObject({ version: VersionSchema });
export const CancelTaskCommandSchema = z.strictObject({
    reason: z.string().trim().max(500).optional(),
    version: VersionSchema,
});
